import BaseComponent from '/webresources-sources/js/components/base-component';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock.es6';
import * as focusTrap from 'focus-trap';
import { defineComponent } from '/webresources-sources/js/util/jquery';

const scrollTop = function (element, to = 0, duration = 1000) {
    const start = element.scrollTop,
        change = to - start,
        startDate = +new Date(),
        // t = current time
        // b = start value
        // c = change in value
        // d = duration
        easeInOutQuad = function (t, b, c, d) {
            t /= d / 2;
            if (t < 1) return (c / 2) * t * t + b;
            t--;
            return (-c / 2) * (t * (t - 2) - 1) + b;
        },
        animateScroll = function () {
            const currentDate = +new Date();
            const currentTime = currentDate - startDate;
            element.scrollTop = parseInt(easeInOutQuad(currentTime, start, change, duration));
            if (currentTime < duration) {
                requestAnimationFrame(animateScroll);
            } else {
                element.scrollTop = to;
            }
        };
    animateScroll();
};

// TODO: maybe we should extend from NavigationComponent in "magnolia/frontend" ?!
export default class NavigationComponent extends BaseComponent {
    properties() {
        return {
            open: false,
            showSearchDialog: false,
            heightMultiplier: 1,
        };
    }

    connected() {
        this.calculateCSSProperties();
        this.height = 0;
        this.lastScrollTop = 0;

        const metaLinks = this.element.querySelectorAll('.navbar-dropdown .meta-links a');
        const socialLinks = this.element.querySelectorAll('.navbar-dropdown .social-links a');
        const mainLinks = this.element.querySelectorAll('.navbar-dropdown a.stretched-link');
        const languageSwitchLinks = this.element.querySelectorAll('.navbar-dropdown .language-switch-container a');
        this._navLinks = [...metaLinks, ...socialLinks, ...mainLinks, ...languageSwitchLinks];
        this._navLinks.forEach((link) => {
            link.setAttribute('tabindex', '-1');
        });

        this._searchActions = this.element.querySelectorAll('[data-search-action]');
        this._searchActions.forEach((element) => {
            element.setAttribute('tabindex', '-1');
        });

        const trapMenuElements = [this.$element.find('.navbar-dropdown')[0]];
        const trapSearchElements = [this.$element.find('.search-dialog-dropdown')[0]];
        if (window.matchMedia('(max-width: 991px)').matches) {
            trapMenuElements.push(this.$element.find('.navbar-items')[0]);
            trapMenuElements.push(this.$element.find('.navbar-scroll-hint')[0]);
            trapSearchElements.push(this.$element.find('.navbar-items')[0]);
        } else {
            trapMenuElements.push(this.$element.find('.navbar-items .menu-and-search-icons')[0]);
            trapSearchElements.push(this.$element.find('.navbar-items .menu-and-search-icons')[0]);
        }

        this._focusTrapMenu = focusTrap.createFocusTrap(trapMenuElements);
        this._focusTrapSearch = focusTrap.createFocusTrap(trapSearchElements);
    }

    watch() {
        return {
            open: (open) => {
                if (open) {
                    disableBodyScroll(this.$element.find('.navbar-dropdown')[0], {
                        reserveScrollBarGap: true,
                    });

                    const scrollBarGap = window.getComputedStyle(document.body)['padding-right'];
                    this.element.style.setProperty('--scroll-bar-gap', scrollBarGap);

                    this.element.classList.add('open');

                    // test if content can scroll
                    const navbarDropdown = this.element.querySelector('.navbar-dropdown');
                    const navContainer = this.element.querySelector('.nav-container');
                    if (navContainer.scrollHeight > window.innerHeight) {
                        this.element.classList.add('can-scroll');
                        navbarDropdown.addEventListener(
                            'scroll',
                            () => {
                                this.element.classList.add('has-scrolled');
                            },
                            { passive: true, once: true },
                        );
                    }

                    this._navLinks.forEach((link) => {
                        link.setAttribute('tabindex', link.getAttribute('data-tabindex') || '0');
                    });

                    this._focusTrapMenu.activate();
                } else {
                    enableBodyScroll(this.$element.find('.navbar-dropdown')[0]);
                    this.element.classList.remove('open');

                    this._focusTrapMenu.deactivate();

                    this._navLinks.forEach((link) => {
                        link.setAttribute('tabindex', '-1');
                    });
                }
                const navbarToggler = this.element.querySelector('.navbar-toggler');
                navbarToggler.setAttribute('aria-expanded', open === true ? 'true' : 'false');
            },
            showSearchDialog: (showSearchDialog) => {
                if (showSearchDialog) {
                    disableBodyScroll(this.$element.find('.search-dialog-dropdown')[0], {
                        reserveScrollBarGap: true,
                    });

                    const scrollBarGap = window.getComputedStyle(document.body)['padding-right'];
                    this.element.style.setProperty('--scroll-bar-gap', scrollBarGap);

                    this.element.classList.add('show-search');

                    this._searchActions.forEach((element) => {
                        element.setAttribute('tabindex', element.getAttribute('data-tabindex') || '0');
                    });

                    this._focusTrapSearch.activate();
                } else {
                    enableBodyScroll(this.$element.find('.search-dialog-dropdown')[0]);
                    this.element.classList.remove('show-search');

                    this._searchActions.forEach((element) => {
                        element.setAttribute('tabindex', '-1');
                    });

                    this._focusTrapSearch.deactivate();
                }
                const searchDialogToggler = this.element.querySelector('.search-dialog-toggler');
                searchDialogToggler.setAttribute('aria-expanded', showSearchDialog === true ? 'true' : 'false');
            },
        };
    }

    events() {
        return {
            document: {
                scroll: () => this.hasScrolled(window.scrollY),
            },
            window: {
                resize: () => this.calculateCSSProperties(),
            },
            '[data-toggle="dropdown"]': {
                click: (e) => {
                    e.preventDefault();
                    this.showSearchDialog = false;
                    this.open = !this.open;
                },
            },
            '[data-toggle="search-dialog"]': {
                click: (e) => {
                    e.preventDefault();
                    this.open = false;
                    this.showSearchDialog = !this.showSearchDialog;
                },
            },
            '.navbar-scroll-hint': {
                click: () => {
                    const navbarDropdown = this.element.querySelector('.navbar-dropdown');
                    scrollTop(navbarDropdown, navbarDropdown.scrollHeight);
                },
            },
        };
    }

    hasScrolled(scrollY) {
        if (scrollY > this.element.offsetHeight * this.heightMultiplier && scrollY > 0) {
            this.element.classList.add('is-sticky');
            this.element.classList.remove('not-sticky');
        } else {
            this.element.classList.add('not-sticky');
            this.element.classList.remove('is-sticky');
        }

        if (scrollY > this.lastScrollTop && scrollY) {
            // Scroll Down
            this.element.classList.remove('going-up');
            this.element.classList.add('going-down');
        } else {
            // Scroll Up
            if (scrollY < this.lastScrollTop) {
                this.element.classList.remove('going-down');
                this.element.classList.add('going-up');
            }
        }

        this.lastScrollTop = scrollY;
    }

    calculateCSSProperties() {
        const headerHeight = this.element.offsetHeight;

        if (!headerHeight) {
            return;
        }

        this.element.style.setProperty('--header-height', headerHeight + 'px');

        // global css properties set on html element
        const html = document.querySelector('html');
        html.style.setProperty('--header-height', headerHeight + 'px');
    }
}

defineComponent('by-tm-navigation-component', NavigationComponent);
